<template>
  <div class="program-document">
    <!-- Off-screen measure pass so we can pack full days per page. -->
    <div class="program-measure" aria-hidden="true">
      <div ref="measureHeader" class="program-page-header">
        <QuoteProgramWrapperHeader :quote="quote" :show-details="false" />
      </div>
      <div
        v-for="(day, dayIndex) in days"
        :key="`measure-day-${dayIndex}`"
        :ref="(el) => setDayMeasureRef(el, dayIndex)"
        class="program-day"
      >
        <div class="program-day__main">
          <div class="program-day__rail">
            <img :src="starsSrc" alt="" class="program-day__stars" />
          </div>
          <div class="program-day__content">
            <div class="program-day__title">
              {{ frenchDayOrdinal(dayIndex).number }}<sup>{{ frenchDayOrdinal(dayIndex).suffix }}</sup> journée
            </div>
            <div class="program-day__cities">{{ day.heading }}</div>
            <div class="program-day__details">
              <div v-if="day.routeId" class="program-day__paragraph">{{ getRouteById(day.routeId) }}</div>
              <div
                v-for="(item, itemIndex) in formattedItems(day)"
                :key="itemIndex"
                :class="item.title ? 'program-day__bullet' : 'program-day__paragraph'"
              >
                <template v-if="item.title">
                  <span class="program-day__bullet-title">{{ item.title }}</span>
                  <span class="program-day__bullet-body">{{ item.body }}</span>
                </template>
                <template v-else>{{ item.body }}</template>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hasDayFooter(day)" class="program-day__footer">
          <div v-if="dayMealsLabel(day)" class="program-day__meals">
            <img :src="mealIconSrc" alt="" class="program-day__footer-icon" />
            <span>{{ dayMealsLabel(day) }}</span>
          </div>
          <div v-if="dayAccommodationName(day)" class="program-day__stay">
            <span>{{ dayAccommodationName(day) }}</span>
            <img :src="stayIconSrc" alt="" class="program-day__footer-icon" />
          </div>
        </div>
      </div>
      <div ref="measureImages" class="program-page-images">
        <div v-for="slot in maxImagesPerPage" :key="`measure-image-${slot}`" class="program-page-image">
          <div class="program-page-image__placeholder"></div>
        </div>
      </div>
    </div>

    <div class="program-cover-page program-page">
      <div class="program-cover-top">
        <QuoteProgramWrapperHeader :quote="quote" :show-details="true" :is-cover="true" />
      </div>

      <div
        class="program-cover-image"
        :class="{ 'editable-area': editMode }"
        @click="editMode && openCoverImageDialog()"
      >
        <v-btn
          v-if="editMode && coverImageId"
          icon
          small
          color="error"
          class="clear-btn"
          @click.stop="clearCoverImage()"
        >
          <v-icon small>close</v-icon>
        </v-btn>
        <img
          v-if="coverImageId"
          :src="getImagePathById(coverImageId)"
          class="program-cover-image__img"
          alt=""
        />
        <div v-else-if="editMode" class="program-cover-image__placeholder">Select a cover image</div>
        <div v-else class="program-cover-image__fallback">
          <img :src="starsSrc" alt="" class="program-cover-image__fallback-stars" />
          <div class="program-cover-image__fallback-line"></div>
        </div>
      </div>
    </div>

    <div class="program-overview-page program-page">
      <QuoteProgramWrapperHeader :quote="quote" :show-details="false" class="program-page-header" />
      <div class="program-overview">
        <section class="program-overview__section">
          <div class="program-overview__heading">
            <img :src="introductionIconSrc" alt="" class="program-overview__icon" />
            <div class="program-overview__title">{{ programIntroductionTitle }}</div>
          </div>
          <div class="program-overview__body">{{ programIntroduction }}</div>
        </section>

        <section class="program-overview__section program-overview__section--table">
          <table class="program-overview__table">
            <colgroup>
              <col class="program-overview__col-day" />
              <col class="program-overview__col-route" />
              <col class="program-overview__col-trip" />
            </colgroup>
            <thead>
              <tr>
                <th>Journée</th>
                <th>Découvertes</th>
                <th>Parcours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in programTableRows" :key="row.dayIndex">
                <td class="program-overview__cell-day">
                  {{ row.ordinal.number }}<sup>{{ row.ordinal.suffix }}</sup>
                </td>
                <td class="program-overview__cell-route">{{ row.routeLabel }}</td>
                <td class="program-overview__cell-trip">{{ row.trip }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="program-overview__section">
          <div class="program-overview__heading">
            <img :src="successesIconSrc" alt="" class="program-overview__icon" />
            <div class="program-overview__title">
              Les succès de <em>Supernature</em> :
            </div>
          </div>
          <ul class="program-overview__list">
            <li v-for="(item, itemIndex) in supernatureSuccessItems" :key="itemIndex" v-html="item"></li>
          </ul>
        </section>
      </div>
    </div>

    <div
      v-for="(page, pageIndex) in dayPages"
      :key="pageIndex"
      class="program-page"
    >
      <QuoteProgramWrapperHeader :quote="quote" :show-details="false" class="program-page-header" />

      <div v-for="entry in page" :key="entry.dayIndex" class="program-day">
        <div class="program-day__main">
          <div class="program-day__rail">
            <img :src="starsSrc" alt="" class="program-day__stars" />
          </div>
          <div class="program-day__content">
            <div class="program-day__title">
              {{ frenchDayOrdinal(entry.dayIndex).number }}<sup>{{ frenchDayOrdinal(entry.dayIndex).suffix }}</sup> journée
            </div>
            <div class="program-day__cities">{{ entry.day.heading }}</div>
            <div class="program-day__details">
              <div v-if="entry.day.routeId" class="program-day__paragraph">{{ getRouteById(entry.day.routeId) }}</div>
              <div
                v-for="(item, itemIndex) in formattedItems(entry.day)"
                :key="itemIndex"
                :class="item.title ? 'program-day__bullet' : 'program-day__paragraph'"
              >
                <template v-if="item.title">
                  <span class="program-day__bullet-title">{{ item.title }}</span>
                  <span class="program-day__bullet-body">{{ item.body }}</span>
                </template>
                <template v-else>{{ item.body }}</template>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hasDayFooter(entry.day)" class="program-day__footer">
          <div v-if="dayMealsLabel(entry.day)" class="program-day__meals">
            <img :src="mealIconSrc" alt="" class="program-day__footer-icon" />
            <span>{{ dayMealsLabel(entry.day) }}</span>
          </div>
          <div v-if="dayAccommodationName(entry.day)" class="program-day__stay">
            <span>{{ dayAccommodationName(entry.day) }}</span>
            <img :src="stayIconSrc" alt="" class="program-day__footer-icon" />
          </div>
        </div>
      </div>

      <template v-if="page.length">
        <div v-if="editMode" class="program-page-images__controls">
          <span class="program-page-images__controls-label">Images :</span>
          <div class="program-page-images__toggle">
            <v-btn
              v-for="count in imageCountOptions"
              :key="`count-${pageIndex}-${count}`"
              x-small
              depressed
              :color="getPageImageCount(pageIndex) === count ? 'primary' : 'grey lighten-3'"
              class="program-page-images__count-btn"
              @click.stop="onPageImageCountChange(pageIndex, count)"
            >
              {{ count }}
            </v-btn>
          </div>
        </div>

        <div
          v-if="pageImageSlots(pageIndex).length"
          :key="`page-images-${pageIndex}-${getPageImageCount(pageIndex)}`"
          class="program-page-images"
        >
        <div
          v-for="(imageId, slotIndex) in pageImageSlots(pageIndex)"
          :key="`image-${pageIndex}-${slotIndex}`"
          class="program-page-image"
          :class="{ 'editable-area': editMode }"
          @click="editMode && openImageDialog(pageIndex, slotIndex)"
        >
          <v-btn
            v-if="editMode && imageId"
            icon
            small
            color="error"
            class="clear-btn"
            @click.stop="clearImage(pageIndex, slotIndex)"
          >
            <v-icon small>close</v-icon>
          </v-btn>
          <img
            v-if="imageId"
            :src="getImagePathById(imageId)"
            class="program-page-image__img"
            alt=""
          />
          <div v-else-if="editMode" class="program-page-image__placeholder">Select an image</div>
          <div v-else class="program-page-image__fallback">
            <img :src="starsSrc" alt="" class="program-page-image__fallback-stars" />
            <div class="program-page-image__fallback-line"></div>
          </div>
        </div>
        </div>
      </template>
    </div>

    <QuoteProgramExtraPages
      :quote="quote"
      :edit-mode="editMode"
      :images="images"
      :program-pages="programPages"
      @update="onExtraPagesUpdate"
    />

    <div class="program-prices-page program-page">
      <QuoteProgramWrapperHeader :quote="quote" :show-details="false" class="program-page-header" />
      <div class="program-prices">
        <h1 class="program-prices__title">Prix de la prestation de voyage</h1>
        <div class="program-prices__intro">
          <img :src="priceIconSrc" alt="" class="program-prices__icon" />
          <div class="program-prices__intro-text">{{ programPriceIntro }}</div>
        </div>

        <div v-if="overviewPriceTable" class="program-prices__tables">
          <table class="program-prices__table">
            <colgroup>
              <col class="program-prices__col-first" />
              <col
                v-for="(header, index) in overviewPriceTable.periodHeaders"
                :key="`price-col-${index}`"
              />
            </colgroup>
            <thead>
              <tr>
                <th>Personnes</th>
                <th v-for="(header, index) in overviewPriceTable.periodHeaders" :key="`price-h-${index}`">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in overviewPriceTable.peopleRows" :key="`pax-${rowIndex}`">
                <td>{{ row.label }}</td>
                <td v-for="(value, colIndex) in row.values" :key="`pax-${rowIndex}-${colIndex}`">
                  {{ formatProgramPrice(value) }}
                </td>
              </tr>
            </tbody>
          </table>

          <table class="program-prices__table program-prices__table--supplements">
            <colgroup>
              <col class="program-prices__col-first" />
              <col
                v-for="(header, index) in overviewPriceTable.periodHeaders"
                :key="`supp-col-${index}`"
              />
            </colgroup>
            <tbody>
              <tr v-for="row in overviewPriceTable.supplementRows" :key="row.key">
                <td class="program-prices__label">{{ row.label }}</td>
                <td v-for="(value, colIndex) in row.values" :key="`${row.key}-${colIndex}`">
                  {{ formatProgramPrice(value) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <section v-if="disclaimerItems.length" class="program-prices__disclaimer">
          <img :src="disclaimerIconSrc" alt="" class="program-prices__icon" />
          <ul class="program-prices__list">
            <li v-for="(item, itemIndex) in disclaimerItems" :key="itemIndex">{{ item }}</li>
          </ul>
        </section>
      </div>
    </div>

    <div class="program-accommodations-page program-page program-page--last">
      <QuoteProgramWrapperHeader :quote="quote" :show-details="false" class="program-page-header" />
      <div class="program-accommodations">
        <h1 class="program-accommodations__title">Hébergements standards (ou similaires)</h1>

        <ul v-if="programAccommodations.length" class="program-accommodations__list">
          <li v-for="item in programAccommodations" :key="item.key" class="program-accommodations__item">
            <div v-if="item.description" class="program-accommodations__description">{{ item.description }}</div>
            <div v-if="item.name" class="program-accommodations__name">{{ item.name }}</div>
            <a
              v-if="item.websiteHref"
              class="program-accommodations__website"
              :href="item.websiteHref"
              target="_blank"
              rel="noopener noreferrer"
            >{{ item.website }}</a>
          </li>
        </ul>
        <div v-else-if="editMode" class="program-accommodations__empty">
          No accommodations found in this quote.
        </div>

        <div v-if="editMode" class="program-page-images__controls program-accommodations__image-controls">
          <span class="program-page-images__controls-label">Images :</span>
          <div class="program-page-images__toggle">
            <v-btn
              v-for="count in imageCountOptions"
              :key="`accommodation-count-${count}`"
              x-small
              depressed
              :color="accommodationImageCount === count ? 'primary' : 'grey lighten-3'"
              class="program-page-images__count-btn"
              @click.stop="onAccommodationImageCountChange(count)"
            >
              {{ count }}
            </v-btn>
          </div>
        </div>

        <div
          v-if="accommodationImageSlots.length"
          class="program-page-images program-accommodations__images"
        >
          <div
            v-for="(imageId, slotIndex) in accommodationImageSlots"
            :key="`accommodation-image-${slotIndex}`"
            class="program-page-image"
            :class="{ 'editable-area': editMode }"
            @click="editMode && openAccommodationImageDialog(slotIndex)"
          >
            <v-btn
              v-if="editMode && imageId"
              icon
              small
              color="error"
              class="clear-btn"
              @click.stop="clearAccommodationImage(slotIndex)"
            >
              <v-icon small>close</v-icon>
            </v-btn>
            <img
              v-if="imageId"
              :src="getImagePathById(imageId)"
              class="program-page-image__img"
              alt=""
            />
            <div v-else-if="editMode" class="program-page-image__placeholder">Select an image</div>
            <div v-else class="program-page-image__fallback">
              <img :src="starsSrc" alt="" class="program-page-image__fallback-stars" />
              <div class="program-page-image__fallback-line"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <QuoteImageDialog v-if="imageDialog" v-model="imageDialog" @select="onImageSelect" />
    <QuoteImageDialog v-if="coverImageDialog" v-model="coverImageDialog" @select="onCoverImageSelect" />
    <QuoteImageDialog
      v-if="accommodationImageDialog"
      v-model="accommodationImageDialog"
      @select="onAccommodationImageSelect"
    />
  </div>
</template>

<script>
import QuoteImageDialog from "./QuoteImageDialog";
import QuoteProgramWrapperHeader from "./QuoteProgramWrapperHeader";
import QuoteProgramExtraPages from "./extraPages/QuoteProgramExtraPages";
import quoteUtils from "@/modules/quotes/components/utils";
import { mapFormDataToBody } from "@/modules/quotes/components/mapper";
import moneyService from "@/services/money";
import { formatRouteLabel } from "@/modules/routes/utils";
import {
  DEFAULT_PAGE_IMAGE_COUNT,
  MAX_IMAGES_PER_PAGE,
  createPageImageBlock,
  getPageImageCount as resolvePageImageCount,
  mergePageImageBlocks,
  migrateFlatPageImages,
  setPageImageAtSlot,
  setPageImageCount,
} from "./programImageBlocks";

function getProgramCurrencyPhrase(env) {
  const currency = moneyService.getCurrencySource(env)?.currency || "USD";
  if (currency === "CAD") return "dollars canadien (cad)";
  if (currency === "JPY") return "yens (jpy)";
  return "dollars américain (usd)";
}

const PAGE_INNER_HEIGHT = 900;
const IMAGE_COUNT_OPTIONS = [0, 1, 2, 3];

function findRouteById(routes, routeId) {
  if (!routeId) return null;
  return (routes || []).find((route) => route && route.id === routeId) || null;
}

function getProgramDayHeading(day, routes) {
  const routeLabel = formatRouteLabel(findRouteById(routes, day?.routeId));
  if (routeLabel) return routeLabel;
  return quoteUtils.formatDayCities(day?.cityStart, day?.cityEnd);
}

function getTableRouteLabel(day, routes) {
  const route = findRouteById(routes, day?.routeId);
  const custom = String(route?.label || "").trim();
  if (custom) return custom;
  return quoteUtils.formatDayCities(day?.cityStart, day?.cityEnd);
}

function formatRouteTrip(route) {
  if (!route) return "";
  const travelTime = String(route.travel_time || "").trim();
  const distance = route.distance;
  const hasDistance = distance != null && distance !== "";
  if (hasDistance && travelTime) return `${distance} km > ${travelTime}`;
  if (hasDistance) return `${distance} km`;
  return travelTime;
}

function withTitleColon(title) {
  const text = String(title || "").trim();
  if (!text) return "";
  return /[:：]\s*$/.test(text) ? text : `${text} :`;
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function formatSuccessLine(text) {
  return escapeHtml(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

export default {
  components: {
    QuoteImageDialog,
    QuoteProgramWrapperHeader,
    QuoteProgramExtraPages,
  },
  props: {
    quote: {
      type: Object,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    program: {
      type: Object,
    },
    routes: {
      type: Array,
      default: () => [],
    },
    images: {
      type: Array,
    },
    programPages: {
      type: Array,
      default: () => [],
    },
    supplierProductData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      imageDialog: null,
      coverImageDialog: null,
      accommodationImageDialog: null,
      packedPageIndexes: null,
      relayoutQueued: false,
      activePageImageBlocks: null,
      maxImagesPerPage: MAX_IMAGES_PER_PAGE,
      imageCountOptions: IMAGE_COUNT_OPTIONS,
      starsSrc: require("@/assets/img/program-stars.png"),
      mealIconSrc: require("@/assets/img/program-meal.png"),
      stayIconSrc: require("@/assets/img/program-accommodation.png"),
      introductionIconSrc: require("@/assets/img/program-introduction.png"),
      successesIconSrc: require("@/assets/img/program-successes.png"),
      priceIconSrc: require("@/assets/img/program-price.png"),
      disclaimerIconSrc: require("@/assets/img/program-disclaimer.png"),
    };
  },
  created() {
    this._dayMeasureEls = [];
    this._lastPackedPagesKey = null;
  },
  computed: {
    days() {
      return (this.quote?.costs || []).map((day) => ({
        ...day,
        heading: getProgramDayHeading(day, this.routes),
      }));
    },
    migratedPageImageBlocks() {
      const legacyFlat = this.quote?.program?.pageImages;
      if (Array.isArray(legacyFlat) && legacyFlat.length) {
        return migrateFlatPageImages(legacyFlat);
      }
      const legacyDayImages = (this.quote?.costs || []).map((day) => day.imageId).filter((imageId) => imageId);
      if (legacyDayImages.length) {
        return migrateFlatPageImages(legacyDayImages);
      }
      return [];
    },
    pageImageBlocksFromQuote() {
      const stored = this.quote?.program?.pageImageBlocks;
      if (!Array.isArray(stored) || !stored.length) {
        return this.migratedPageImageBlocks;
      }
      return mergePageImageBlocks(stored, this.migratedPageImageBlocks);
    },
    resolvedPageImageBlocks() {
      return this.activePageImageBlocks || this.pageImageBlocksFromQuote;
    },
    coverImageId() {
      return this.quote?.program?.coverImageId ?? null;
    },
    programAccommodations() {
      return quoteUtils.getProgramAccommodations(this.quote, this.supplierProductData);
    },
    accommodationImageBlock() {
      const stored = this.quote?.program?.accommodationImageBlock;
      if (stored && typeof stored === "object") {
        return createPageImageBlock(stored.imageCount ?? stored.image_count, stored.imageIds ?? stored.image_ids ?? []);
      }
      return createPageImageBlock(DEFAULT_PAGE_IMAGE_COUNT, []);
    },
    accommodationImageCount() {
      return this.accommodationImageBlock.imageCount;
    },
    accommodationImageSlots() {
      const block = this.accommodationImageBlock;
      return (block.imageIds || []).slice(0, block.imageCount);
    },
    programIntroduction() {
      return String(this.quote?.program?.introduction || "").trim();
    },
    programIntroductionTitle() {
      return withTitleColon(this.quote?.title);
    },
    supernatureSuccesses() {
      return String(this.quote?.program?.supernatureSuccesses || "").trim();
    },
    supernatureSuccessItems() {
      return this.supernatureSuccesses
        .split(/\n/)
        .map((line) => line.replace(/^[-•]\s*/, "").trim())
        .filter((line) => line.length > 0)
        .map((line) => formatSuccessLine(line));
    },
    programTableRows() {
      return this.days.map((day, dayIndex) => ({
        dayIndex,
        ordinal: this.frenchDayOrdinal(dayIndex),
        routeLabel: getTableRouteLabel(day, this.routes),
        trip: formatRouteTrip(findRouteById(this.routes, day.routeId)),
      }));
    },
    quoteEnvironment() {
      return this.quote?.environment || this.$store.getters["AccountStore/environment"];
    },
    overviewPriceTable() {
      if (!this.quote?.client?.id) return null;
      try {
        const body = mapFormDataToBody(
          {
            ...this.quote,
            costs: this.quote.costs || [],
            logisticsCosts: this.quote.logisticsCosts || [],
            peopleRanges: this.quote.peopleRanges || [],
            accommodations: this.quote.accommodations || [],
            comparisonPeriods: this.quote.comparisonPeriods || [],
            margin: this.quote.margin || {
              status: "fixed",
              percentFixed: 0,
              percentDynamic: [],
              perPax: [],
              perGroup: [],
            },
            overviewOptions: this.quote.overviewOptions || {},
            program: this.quote.program || {},
          },
          this.quoteEnvironment
        );
        const table = quoteUtils.getQuoteOverviewPriceTable(
          this.quoteEnvironment,
          this.supplierProductData || {},
          body
        );
        if (!table?.peopleRows?.length) return null;
        return table;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    programPriceIntro() {
      const year = this.overviewPriceTable?.year || this.quote?.period?.startYear || "";
      const currencyPhrase = getProgramCurrencyPhrase(this.quoteEnvironment);
      return `Prix ${year} net en ${currencyPhrase} par personne, en occupation double ou twin`;
    },
    disclaimerItems() {
      return String(this.quote?.overviewOptions?.disclaimer || "")
        .split(/\n/)
        .map((line) => line.replace(/^[-•]\s*/, "").trim())
        .filter((line) => line.length > 0);
    },
    dayPages() {
      const indexes = this.packedPageIndexes;
      if (!indexes || !indexes.length) {
        if (!this.days.length) return [[]];
        return this.days.map((day, dayIndex) => [{ day, dayIndex }]);
      }
      return indexes.map((pageIndexes) =>
        pageIndexes.map((dayIndex) => ({
          day: this.days[dayIndex],
          dayIndex,
        }))
      );
    },
  },
  watch: {
    days: {
      handler() {
        this.queueRelayout();
      },
      deep: true,
    },
    editMode() {
      this.queueRelayout();
    },
    routes: {
      handler() {
        this.queueRelayout();
      },
      deep: true,
    },
    "quote.version"() {
      this.resetActivePageImageBlocks();
    },
    pageImageBlocksFromQuote: {
      handler(blocks) {
        if (!this.activePageImageBlocks?.length && blocks?.length) {
          this.activePageImageBlocks = this.clonePageImageBlocks(blocks);
        }
      },
      immediate: true,
    },
  },
  methods: {
    frenchDayOrdinal(dayIndex) {
      const dayNumber = dayIndex + 1;
      if (dayNumber === 1) {
        return { number: 1, suffix: "ère" };
      }
      return { number: dayNumber, suffix: "ème" };
    },
    formatProgramPrice(value) {
      if (value === "" || value == null || Number.isNaN(Number(value))) return "";
      return Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 0 });
    },
    visibleItems(day) {
      return (day?.items || []).filter((item) => this.shouldShowItem(item));
    },
    parseDescription(text) {
      const trimmed = (text || "").trim();
      const match = trimmed.match(/^([^:\n]{1,100}):\s+([\s\S]+)$/);
      if (match) {
        return {
          title: `${match[1].trim()}:`,
          body: match[2].trim(),
        };
      }
      return { title: null, body: trimmed };
    },
    formattedItems(day) {
      return this.visibleItems(day).map((item) => {
        const title = String(item.productProgramTitle || "").trim();
        const description = item.productProgramDescription || "";
        if (title) {
          const body = description.trim() === "-" ? "" : description.trim();
          return {
            title: `${title.replace(/:+$/, "")}:`,
            body,
          };
        }
        return this.parseDescription(description);
      });
    },
    dayMealsLabel(day) {
      const parts = [];
      if (day?.breakfast) parts.push("Petit-déjeuner");
      if (day?.lunch) parts.push(parts.length ? "déjeuner" : "Déjeuner");
      if (day?.dinner) parts.push(parts.length ? "dîner" : "Dîner");
      if (!parts.length) return "";
      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} et ${parts[1]}`;
      return `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}`;
    },
    dayAccommodationName(day) {
      const rooms = (day?.items || []).filter((item) => item.productType === "ROOM");
      for (let index = rooms.length - 1; index >= 0; index--) {
        const name = String(rooms[index].programSupplierName || "").trim();
        if (name) return name;
      }
      return "";
    },
    hasDayFooter(day) {
      return !!(this.dayMealsLabel(day) || this.dayAccommodationName(day));
    },
    getPageImageCount(pageIndex) {
      return resolvePageImageCount(this.resolvedPageImageBlocks, pageIndex, DEFAULT_PAGE_IMAGE_COUNT);
    },
    pageImageSlots(pageIndex) {
      const count = this.getPageImageCount(pageIndex);
      if (count === 0) return [];
      const ids = this.resolvedPageImageBlocks[pageIndex]?.imageIds || [];
      return Array.from({ length: count }, (_, slotIndex) => ids[slotIndex] || null);
    },
    resetActivePageImageBlocks() {
      this.activePageImageBlocks = this.clonePageImageBlocks(this.pageImageBlocksFromQuote);
    },
    setDayMeasureRef(el, dayIndex) {
      // Keep measure refs off the reactive state to avoid render loops.
      if (el) {
        this._dayMeasureEls[dayIndex] = el;
      }
    },
    queueRelayout() {
      if (this.relayoutQueued) return;
      this.relayoutQueued = true;
      this.$nextTick(() => {
        this.relayoutQueued = false;
        this.relayoutPages();
      });
    },
    relayoutPages() {
      if (!this.days.length) {
        this.setPackedPageIndexes([[]]);
        return;
      }

      const headerEl = this.$refs.measureHeader;
      const imagesEl = this.$refs.measureImages;
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
      const imagesHeight = imagesEl ? imagesEl.getBoundingClientRect().height : 196;
      const gap = 16;

      const dayHeights = this.days.map((_, dayIndex) => {
        const el = this._dayMeasureEls[dayIndex];
        return el ? Math.max(el.getBoundingClientRect().height, 40) : 120;
      });

      const pages = [];
      let dayIndex = 0;

      while (dayIndex < this.days.length) {
        const pageIdx = pages.length;
        const pageImageCount = this.getPageImageCount(pageIdx);
        const footerHeight = pageImageCount > 0 ? imagesHeight : 0;
        const availableForDays = Math.max(120, PAGE_INNER_HEIGHT - headerHeight - footerHeight - gap);

        const current = [];
        let used = 0;

        while (dayIndex < this.days.length) {
          const dayHeight = dayHeights[dayIndex];
          if (current.length > 0 && used + dayHeight > availableForDays) {
            break;
          }
          current.push(dayIndex);
          used += dayHeight;
          dayIndex++;
        }

        pages.push(current);
      }

      this.setPackedPageIndexes(pages);
    },
    setPackedPageIndexes(pages) {
      const nextKey = JSON.stringify(pages);
      if (nextKey === this._lastPackedPagesKey) return;
      this._lastPackedPagesKey = nextKey;
      this.packedPageIndexes = pages;
    },
    getImagePathById(imageId) {
      if (imageId == null || !Array.isArray(this.images)) return "";
      const image = this.images.find((img) => img && String(img.id) === String(imageId));
      if (!image?.name) return "";
      return `${this.$store.getters["host"]}storage/images/${image.name}`;
    },
    getRouteById(routeId) {
      const route = findRouteById(this.routes, routeId);
      if (!route) return "";
      return route.description;
    },
    openImageDialog(pageIndex, slotIndex) {
      this.imageDialog = {
        imageId: this.pageImageSlots(pageIndex)[slotIndex],
        pageIndex,
        slotIndex,
      };
    },
    openCoverImageDialog() {
      this.coverImageDialog = {
        imageId: this.coverImageId,
      };
    },
    updateCoverImage(imageId) {
      return {
        ...this.quote,
        program: {
          ...(this.quote.program || {}),
          coverImageId: imageId,
        },
      };
    },
    onCoverImageSelect(image) {
      const updatedQuote = this.updateCoverImage(image.id);
      this.$emit("update", { quote: updatedQuote, image: image });
    },
    clearCoverImage() {
      const updatedQuote = this.updateCoverImage(null);
      this.$emit("update", { quote: updatedQuote });
    },
    openAccommodationImageDialog(slotIndex) {
      this.accommodationImageDialog = {
        imageId: this.accommodationImageSlots[slotIndex],
        slotIndex,
      };
    },
    updateAccommodationImageBlock(block) {
      return {
        ...this.quote,
        program: {
          ...(this.quote.program || {}),
          accommodationImageBlock: createPageImageBlock(
            block?.imageCount ?? block?.image_count,
            block?.imageIds ?? block?.image_ids ?? []
          ),
        },
      };
    },
    onAccommodationImageCountChange(imageCount) {
      const current = this.accommodationImageBlock;
      const next = createPageImageBlock(imageCount, current.imageIds || []);
      this.$emit("update", { quote: this.updateAccommodationImageBlock(next) });
    },
    onAccommodationImageSelect(image) {
      if (!this.accommodationImageDialog) return;
      const { slotIndex } = this.accommodationImageDialog;
      const current = this.accommodationImageBlock;
      const imageIds = [...(current.imageIds || [])];
      while (imageIds.length < current.imageCount) imageIds.push(null);
      if (slotIndex >= 0 && slotIndex < current.imageCount) {
        imageIds[slotIndex] = image.id;
      }
      const updatedQuote = this.updateAccommodationImageBlock({
        imageCount: current.imageCount,
        imageIds,
      });
      this.$emit("update", { quote: updatedQuote, image });
      this.accommodationImageDialog = null;
    },
    clearAccommodationImage(slotIndex) {
      const current = this.accommodationImageBlock;
      const imageIds = [...(current.imageIds || [])];
      while (imageIds.length < current.imageCount) imageIds.push(null);
      if (slotIndex >= 0 && slotIndex < current.imageCount) {
        imageIds[slotIndex] = null;
      }
      this.$emit("update", {
        quote: this.updateAccommodationImageBlock({
          imageCount: current.imageCount,
          imageIds,
        }),
      });
    },
    clonePageImageBlocks(blocks) {
      return (blocks || []).map((block) => ({
        imageCount: block?.imageCount ?? block?.image_count ?? DEFAULT_PAGE_IMAGE_COUNT,
        imageIds: [...(block?.imageIds ?? block?.image_ids ?? [])],
      }));
    },
    onPageImageCountChange(pageIndex, imageCount) {
      const pageCount = Math.max(this.dayPages.length, pageIndex + 1, this.resolvedPageImageBlocks.length);
      const blocks = setPageImageCount(
        this.clonePageImageBlocks(this.resolvedPageImageBlocks),
        pageIndex,
        imageCount,
        pageCount
      );
      this.activePageImageBlocks = blocks;
      const updatedQuote = {
        ...this.quote,
        program: {
          ...(this.quote.program || {}),
          pageImageBlocks: blocks,
        },
      };
      this.$emit("update", { quote: updatedQuote });
      this.$nextTick(() => this.queueRelayout());
    },
    updatePageImage(pageIndex, slotIndex, imageId) {
      const pageCount = Math.max(this.dayPages.length, pageIndex + 1, this.resolvedPageImageBlocks.length);
      const blocks = setPageImageAtSlot(
        this.clonePageImageBlocks(this.resolvedPageImageBlocks),
        pageIndex,
        slotIndex,
        imageId,
        pageCount
      );
      this.activePageImageBlocks = blocks;
      return {
        ...this.quote,
        program: {
          ...(this.quote.program || {}),
          pageImageBlocks: blocks,
        },
      };
    },
    onImageSelect(image) {
      const { pageIndex, slotIndex } = this.imageDialog;
      const updatedQuote = this.updatePageImage(pageIndex, slotIndex, image.id);
      this.$emit("update", { quote: updatedQuote, image: image });
    },
    clearImage(pageIndex, slotIndex) {
      const updatedQuote = this.updatePageImage(pageIndex, slotIndex, null);
      this.$emit("update", { quote: updatedQuote });
    },
    onExtraPagesUpdate(payload) {
      this.$emit("update", payload);
    },
    shouldShowItem(item) {
      const title = String(item.productProgramTitle || "").trim();
      const description = (item.productProgramDescription || "").trim();
      const hasDescription = description.length > 0 && description !== "-";
      return item.isShowInProgram && (hasDescription || title.length > 0);
    },
  },
  mounted() {
    this.resetActivePageImageBlocks();
    this.queueRelayout();
    this._onResize = () => this.queueRelayout();
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
    }
  },
};
</script>

<style scoped>
.program-document {
  display: flex;
  flex-direction: column;
  gap: 28px;
  position: relative;
}

.program-measure {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 732px;
  visibility: hidden;
  pointer-events: none;
}

.program-page {
  display: flex;
  flex-direction: column;
  width: 780px;
  min-height: 980px;
  padding: 40px 44px 36px;
  background: #fff;
  border: 1px solid #cfcfcf;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  page-break-after: always;
  break-after: page;
}

.program-page--last {
  page-break-after: auto;
  break-after: auto;
}

.program-page-header {
  margin-bottom: 28px;
  flex: 0 0 auto;
}

.program-cover-page {
  align-items: center;
  padding-top: 52px;
}

.program-overview-page {
  align-items: stretch;
}

.program-prices-page {
  align-items: stretch;
}

.program-overview {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
}

.program-overview__section {
  min-width: 0;
}

.program-overview__section--table {
  margin-top: 36px;
  /* Smaller than top: the successes block starts with a 52px icon heading,
     which otherwise makes the gap after the table look larger. */
  margin-bottom: 16px;
}

.program-overview__heading {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.program-overview__icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex: 0 0 auto;
  display: block;
}

.program-overview__title {
  font-family: "Calibri", sans-serif;
  font-size: 13.5pt;
  font-weight: 700;
  color: #c5a069;
  line-height: 1.25;
  padding-top: 8px;
}

.program-overview__title em {
  font-style: italic;
  font-weight: 700;
}

.program-overview__body {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #333333;
  white-space: pre-line;
  text-align: justify;
  text-align-last: left;
  line-height: 1.4;
  padding-left: 22px;
}

.program-overview__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #333333;
  margin: 0;
}

.program-overview__col-day {
  width: 18%;
}

.program-overview__col-route {
  width: 54%;
}

.program-overview__col-trip {
  width: 28%;
}

.program-overview__table th,
.program-overview__table td {
  box-sizing: border-box;
  padding: 7px 10px;
  vertical-align: middle;
}

.program-overview__table th {
  font-weight: 700;
  text-align: center;
}

.program-overview__table th:not(:last-child),
.program-overview__table td:not(:last-child) {
  border-right: 1px solid #bdbdbd;
}

.program-overview__table td {
  border-bottom: 1px solid #d0d0d0;
}

.program-overview__table tbody tr:last-child td {
  border-bottom: none;
}

.program-overview__cell-day {
  text-align: center;
}

.program-overview__cell-day sup {
  font-size: 0.65em;
}

.program-overview__cell-route {
  text-align: left;
}

.program-overview__cell-trip {
  text-align: center;
  white-space: nowrap;
}

.program-overview__list {
  margin: 0;
  padding-left: 22px;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #333333;
  line-height: 1.45;
}

.program-overview__list li {
  margin-bottom: 6px;
  text-align: justify;
  text-align-last: left;
}

.program-overview__list li ::marker {
  color: #333333;
}

.program-overview__list strong {
  font-weight: 700;
}

.program-prices {
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
}

.program-prices__title {
  margin: 0;
  font-family: "Calibri", sans-serif;
  font-size: 22pt;
  font-weight: 400;
  color: #c5a069;
  text-align: center;
  line-height: 1.2;
  padding-bottom: 10px;
  border-bottom: 1px solid #d7c4a4;
}

.program-prices__intro,
.program-prices__disclaimer {
  display: flex;
  gap: 10px;
}

.program-prices__intro {
  align-items: center;
}

.program-prices__disclaimer {
  align-items: flex-start;
}

.program-prices__icon {
  width: 64px;
  height: 64px;
  object-fit: contain;
  flex: 0 0 auto;
  display: block;
}

.program-prices__intro-text {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  color: #333333;
  line-height: 1.35;
  flex: 1 1 auto;
  min-width: 0;
  text-align: justify;
  text-align-last: left;
}

.program-prices__tables {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  width: 100%;
}

.program-prices__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #333333;
}

.program-prices__table--supplements {
  font-size: 9.5pt;
}

.program-prices__col-first {
  width: 24%;
}

.program-prices__table th,
.program-prices__table td {
  box-sizing: border-box;
  padding: 7px 8px;
  vertical-align: middle;
  text-align: center;
}

.program-prices__table--supplements th,
.program-prices__table--supplements td {
  padding: 5px 6px;
}

.program-prices__table th:first-child,
.program-prices__table td:first-child {
  width: 24%;
}

.program-prices__table th {
  font-weight: 700;
  color: #c5a069;
  padding-top: 0;
  padding-bottom: 8px;
}

.program-prices__table th:not(:last-child),
.program-prices__table td:not(:last-child) {
  border-right: 1px solid #bdbdbd;
}

.program-prices__table td {
  border-bottom: 1px solid #d0d0d0;
}

.program-prices__table tbody tr:last-child td {
  border-bottom: none;
}

.program-prices__label {
  text-align: left;
}

.program-prices__list {
  margin: 0;
  padding-left: 18px;
  font-family: "Calibri", sans-serif;
  font-size: 8.5pt;
  color: #777777;
  line-height: 1.25;
  flex: 1 1 auto;
  min-width: 0;
}

.program-prices__list li {
  margin-bottom: 2px;
  text-align: justify;
  text-align-last: left;
}

.program-accommodations-page {
  align-items: stretch;
}

.program-accommodations {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
}

.program-accommodations__title {
  margin: 0;
  font-family: "Calibri", sans-serif;
  font-size: 22pt;
  font-weight: 400;
  color: #c5a069;
  text-align: center;
  line-height: 1.2;
  padding-bottom: 10px;
  border-bottom: 1px solid #111;
}

.program-accommodations__list {
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
}

.program-accommodations__item {
  position: relative;
  padding-left: 18px;
  margin: 0 0 18px;
}

.program-accommodations__item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c5a36d;
}

.program-accommodations__description {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  font-weight: 700;
  color: #333333;
  line-height: 1.3;
}

.program-accommodations__name {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  font-weight: 400;
  color: #777777;
  line-height: 1.35;
  margin-top: 2px;
}

.program-accommodations__website {
  display: inline-block;
  margin-top: 2px;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  font-weight: 400;
  color: #c5a069;
  text-decoration: underline;
  line-height: 1.35;
}

.program-accommodations__empty {
  margin-top: 28px;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #9e9e9e;
}

.program-accommodations__image-controls {
  margin-top: auto;
  padding-top: 18px;
}

.program-accommodations__images {
  margin-top: auto;
  padding-top: 18px;
}

.program-accommodations__image-controls + .program-accommodations__images {
  margin-top: 0;
  padding-top: 0;
}

.program-cover-top {
  width: 100%;
  flex: 0 0 auto;
  text-align: center;
}

.program-cover-image {
  position: relative;
  width: 100%;
  flex: 0 0 auto;
  margin-top: 56px;
  overflow: hidden;
  background: #fff;
}

.program-cover-image__img {
  width: 100%;
  min-width: 100%;
  height: 320px;
  max-height: 380px;
  object-fit: cover;
  object-position: center;
  display: block;
}

.program-cover-image__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 320px;
  text-align: center;
  color: #9e9e9e;
  font-size: 14px;
  background: #f5f5f5;
}

.program-cover-image__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
  height: 320px;
  box-sizing: border-box;
  border: 1px solid #e4d4b5;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0.15) 45%, transparent 100%),
    linear-gradient(135deg, #f7f1e6 0%, #efe2c8 48%, #e6d3ae 100%);
}

.program-cover-image__fallback-stars {
  width: 42px;
  height: 42px;
  object-fit: contain;
  opacity: 0.92;
}

.program-cover-image__fallback-line {
  width: 48px;
  height: 1.5px;
  background: #c5a36d;
  opacity: 0.7;
}

.program-day {
  display: flex;
  flex-direction: column;
  margin-bottom: 28px;
  page-break-inside: avoid;
  break-inside: avoid;
}

.program-day__main {
  display: grid;
  grid-template-columns: 40px 1fr;
  column-gap: 14px;
  align-items: stretch;
}

.program-day__rail {
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  box-sizing: border-box;
  border-right: 2px solid #c5a36d;
  padding-right: 7px;
  width: 100%;
  align-self: stretch;
  min-height: 36px;
}

.program-day__stars {
  position: static;
  width: 32px;
  height: 32px;
  margin-top: -1px;
  object-fit: contain;
  display: block;
  flex: 0 0 auto;
}

.program-day__content {
  min-width: 0;
}

.program-day__details {
  margin-left: 0;
}

.program-day__title {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  font-weight: 700;
  color: #333333;
  line-height: 1.25;
  margin-bottom: 6px;
}

.program-day__title sup {
  font-size: 0.65em;
}

.program-day__cities {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  font-weight: 700;
  color: #333333;
  margin-bottom: 14px;
  line-height: 1.3;
}

.program-day__paragraph {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #777777;
  white-space: pre-line;
  margin-bottom: 8px;
  line-height: 1.35;
  text-align: justify;
  text-align-last: left;
}

.program-day__placeholder {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #9e9e9e;
  margin-bottom: 8px;
}

.program-day__bullet {
  position: relative;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #777777;
  white-space: pre-line;
  margin: 0 0 8px 10px;
  padding-left: 14px;
  line-height: 1.35;
  text-align: justify;
  text-align-last: left;
}

.program-day__bullet::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.45em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c5a36d;
}

.program-day__bullet-title {
  font-weight: 700;
  color: #c5a36d;
  margin-right: 4px;
}

.program-day__bullet-body {
  color: #777777;
}

.program-day__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 22px;
  padding-left: 54px;
}

.program-day__meals,
.program-day__stay {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #777777;
  line-height: 1.3;
}

.program-day__stay {
  margin-left: auto;
  text-align: right;
}

.program-day__footer-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex: 0 0 auto;
  display: block;
}

.program-page-images {
  display: flex;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  flex: 0 0 auto;
  page-break-inside: avoid;
  page-break-before: avoid;
  break-inside: avoid;
  break-before: avoid;
}

.program-page-images__controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 12px;
  flex: 0 0 auto;
}

.program-page-images__controls-label {
  font-family: "Calibri", sans-serif;
  font-size: 10pt;
  color: #777777;
}

.program-page-images__toggle {
  display: flex;
  gap: 4px;
  flex: 0 0 auto;
}

.program-page-images__count-btn {
  min-width: 28px !important;
  padding: 0 8px !important;
}

.program-page-image {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  height: 180px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.program-page-image__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.program-page-image__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px;
  text-align: center;
  color: #9e9e9e;
  font-size: 12px;
  background: #f5f5f5;
}

.program-page-image__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid #e4d4b5;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0.15) 45%, transparent 100%),
    linear-gradient(135deg, #f7f1e6 0%, #efe2c8 48%, #e6d3ae 100%);
}

.program-page-image__fallback-stars {
  width: 42px;
  height: 42px;
  object-fit: contain;
  opacity: 0.92;
}

.program-page-image__fallback-line {
  width: 48px;
  height: 1.5px;
  background: #c5a36d;
  opacity: 0.7;
}

.editable-area {
  border: 1px solid black;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  background-color: white;
}

.editable-area:hover {
  background-color: rgba(0, 0, 0, 0.05);
  border: 1px solid black;
}

.program-extra-page__inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.program-extra-page__title-block {
  text-align: center;
  margin: 4px 0 24px;
}

.program-extra-page__eyebrow {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  font-weight: 400;
  color: #c5a069;
  line-height: 1.4;
  margin: 0 0 8px;
}

.program-extra-page__title {
  font-family: "Calibri", sans-serif;
  font-size: 16pt;
  font-weight: 700;
  margin: 0 auto;
  max-width: 92%;
  color: #c5a069;
  line-height: 1.35;
  white-space: pre-line;
}

.program-extra-page__title-rule {
  width: 86%;
  max-width: 640px;
  height: 1px;
  margin: 16px auto 0;
  background: #111;
}

.program-extra-page__html {
  font-size: 13px;
  line-height: 1.35;
  color: #2a2a2a;
}

.program-extra-page__html >>> h1,
.program-extra-page__html >>> h2,
.program-extra-page__html >>> h3 {
  margin: 0 0 10px;
  font-weight: 700;
}

.program-extra-page__html >>> p {
  margin: 0 0 10px;
}

.program-extra-page__html >>> ul,
.program-extra-page__html >>> ol {
  margin: 0 0 12px;
  padding-left: 20px;
}

.program-extra-page__html >>> img {
  display: inline-block;
  vertical-align: top;
  max-width: 100%;
  height: auto;
  margin: 4px 6px 8px 0;
}

.program-extra-page__html >>> .rte-columns {
  display: flex;
  gap: 16px;
  width: 100%;
  margin: 12px 0;
  align-items: flex-start;
  box-sizing: border-box;
}

.program-extra-page__html >>> .rte-columns--icon > .rte-column:first-child {
  flex: 0 0 72px;
  max-width: 72px;
  min-width: 72px;
}

.program-extra-page__html >>> .rte-columns--icon > .rte-column:last-child {
  flex: 1 1 auto;
}

.program-extra-page__html >>> .rte-columns--icon > .rte-column:first-child img {
  width: 100%;
  max-width: 64px;
  height: auto;
  margin: 0;
}

.program-extra-page__html >>> .rte-column {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
}

.program-extra-page--map .program-extra-page__inner {
  padding: 0 16px 16px;
}

.program-extra-page--map .program-extra-page__html >>> img {
  width: 100%;
  max-height: 780px;
  object-fit: contain;
}

.program-extra-page__heading {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 8px;
}

.program-extra-page__paragraph {
  font-size: 13px;
  line-height: 1.35;
  margin: 0 0 12px;
  white-space: pre-wrap;
}
</style>
